import { useId } from 'react'

import { useTranslation } from '@molecule/app-react'
import { moleculeSpinnerMarkSvg } from '@molecule/app-ui'

const SIZES = {
  xs: 12,
  sm: 16,
  md: 24,
  lg: 32,
  xl: 48,
} as const

type SpinnerSize = keyof typeof SIZES

/*
 * The molecule spinner — a thin React wrapper around the platform's SINGLE
 * spinner source, `moleculeSpinnerMarkSvg` in @molecule/app-ui: the same
 * geometry, motion (3-phase atom swap, 2.5s cycle / 5s spin) and paint (blue
 * flow + swap-synced atom glints) the IDE, the AI streaming indicator and
 * the deployed-app attribution badge render. This file adds only the React
 * surface (size presets, the i18n loading label, unique gradient ids, the
 * monochrome fallback via `gradient={false}`).
 */
export function MoleculeSpinner({
  size = 'md',
  ariaLabel,
  gradient = true,
}: {
  size?: SpinnerSize | number
  ariaLabel?: string
  gradient?: boolean
}) {
  const { t } = useTranslation()
  const px = typeof size === 'number' ? size : SIZES[size]
  const gradientId = `mol-spinner-gradient-${useId().replace(/[:]/g, '')}`

  const html = moleculeSpinnerMarkSvg({
    size: px,
    gradientId,
    paint: gradient ? 'gradient' : 'mono',
    ariaLabel: ariaLabel ?? t('common.loading', {}, { defaultValue: 'Loading' }),
  })

  return (
    <span
      style={{ display: 'inline-flex', color: 'var(--mol-color-primary, #4070e0)' }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}
